import { useDebounceFn, useMount } from 'ahooks';
import { Collapse, Form, FormInstance, Radio, Select, message } from 'antd';
import { FileDeleteIcon } from 'components/Icons';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMoney } from 'components/RevxMoney';
import { RevxSpin } from 'components/RevxSpin';
import { UnderlineText } from 'components/RevxTypography';
import {
  UnitPricePricingModel,
  UnitPriceSupplier,
  UnitPriceSupplierList,
  UnitPriceSupplierTier,
  UnitPriceSupplierTierRange,
  UnitPriceSupplierTiersOption,
  UnitPricePricingMethod,
  UnitPriceSupplierFixedQuantity,
  UnitPricePreferredSupplier,
} from 'domains/skuDomain';
import { clone, cloneDeep, isArray, pull } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { DEBOUNCE_TIME, NOT_APPLICABLE } from 'utils/constants';
import { PriceProps } from './UnitPrice';
import './UnitPrice.less';
import {
  AutoSplitQuantity,
  Ranges,
  initRange,
  rangeTypeEnum,
} from './useAutoSplitQuantity';

const initSupplierTier = (
  orgId?: number,
  ranges?: UnitPriceSupplierTierRange[],
  fixedQuantity?: UnitPriceSupplierFixedQuantity[]
): UnitPriceSupplierTier => ({
  option: UnitPriceSupplierTiersOption.STANDALONE,
  orgId: orgId || undefined,
  quantityRanges: ranges || [],
  fixedQuantity: fixedQuantity || [],
});

/* The fixed quantity grid shows one line per quantity with a column per supplier, so every supplier carries an
   entry for every quantity on screen - a blank one where that supplier does not price it. Skus priced before the
   grid existed have a quantity list of their own per supplier, they are merged into the lines of the grid here. */
export const alignFixedQuantityGrid = (
  data: UnitPriceSupplier
): UnitPriceSupplier => {
  if (!data || data.pricingMethod !== UnitPricePricingMethod.FIXED_QUANTITY) {
    return data;
  }
  const suppliers = data.suppliers || [];
  const quantities = Array.from(
    new Set(
      suppliers.flatMap((supplier) =>
        (supplier.fixedQuantity || [])
          .map((i) => i.quantity)
          .filter((q) => q !== null && q !== undefined)
      )
    )
  ).sort((a, b) => a - b);
  const storedPreferred = data.preferredSuppliers || [];
  return {
    ...data,
    suppliers: suppliers.map((supplier) => ({
      ...supplier,
      fixedQuantity: quantities.map(
        (quantity) =>
          (supplier.fixedQuantity || []).find(
            (i) => i.quantity === quantity
          ) || {
            quantity,
            priceToPaySupplier: null,
            pricingEnginePrice: null,
            id: Math.random(),
          }
      ),
    })),
    preferredSuppliers: quantities.map((quantity) => ({
      quantity,
      orgId: storedPreferred.find((i) => i.quantity === quantity)?.orgId,
    })),
  };
};

export type OptionsValueType = { value: any; label: any; disabled: boolean };
export const getOptionsList = (
  l: UnitPriceSupplierList[]
): OptionsValueType[] => {
  return l.map((i) => ({
    label: i.orgName,
    value: i.orgId,
    disabled: false,
  }));
};
const MAX_QUANTITY = 10000;

export const SupplierPrice = ({
  skuId,
  header,
  isPart,
  footer,
}: PriceProps) => {
  const [tierData, setTierData] = useState<UnitPriceSupplierTier[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<OptionsValueType[]>(
    []
  );
  const [selectSupplierOptions, setSelectSupplierOptions] = useState<number[]>(
    []
  );
  const [showRanges, setShowRanges] = useState<boolean>(false);
  const [pricingMethod, setPricingMethod] = useState<UnitPricePricingMethod>(
    UnitPricePricingMethod.QUANTITY_RANGE
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [collapseKeys, setCollapseKeys] = useState<string[]>([]);
  const { t } = useTranslation();
  const [supplierUnitPriceForm] = Form.useForm();
  /* keeps what was filled in under Unit price so switching to the pricing
     engine and back does not force the user to type everything again */
  const unitPriceCacheRef = useRef<{
    suppliers: UnitPriceSupplierTier[];
    maximumQuantity?: number;
    pricingMethod?: UnitPricePricingMethod;
  } | null>(null);

  useMount(async () => {
    setLoading(true);
    const list = await skuService.getUnitPriceSupplierList(skuId, true);
    const formatList = getOptionsList(list);
    setSupplierOptions(formatList);
    await getSupplierPrice();
    setLoading(false);
  });

  useEffect(() => {
    supplierOptions.forEach((i, index) => {
      supplierOptions[index].disabled = selectSupplierOptions?.includes(
        i.value
      );
    });
    setSupplierOptions(supplierOptions);
  }, [selectSupplierOptions, supplierOptions]);

  const initSupplierTierQuantityRange = (
    id: number
  ): UnitPriceSupplierTierRange => {
    return {
      highest:
        supplierUnitPriceForm.getFieldsValue().maximumQuantity || MAX_QUANTITY,
      lowest: 1,
      priceToPaySupplier: 0,
      pricingEnginePrice: null,
      id: id,
    };
  };

  const initSupplierTierFixedQuantity = (
    id: number
  ): UnitPriceSupplierFixedQuantity => {
    return {
      quantity: 1,
      priceToPaySupplier: 0,
      pricingEnginePrice: null,
      id: id,
    };
  };

  const getSupplierPrice = async () => {
    const data = alignFixedQuantityGrid(
      await skuService.getSupplierUnitPrice(skuId)
    );
    if (data) {
      supplierUnitPriceForm.setFieldsValue(data);
      setTierData(data?.suppliers);
      setShowRanges(data.pricingModel === UnitPricePricingModel.UNIT_PRICE);
      setPricingMethod(
        data.pricingMethod || UnitPricePricingMethod.QUANTITY_RANGE
      );
    } else {
      supplierUnitPriceForm.setFieldsValue({
        pricingModel: UnitPricePricingModel.PRICING_ENGINE,
        suppliers: [],
        preferredSuppliers: [],
        maximumQuantity: MAX_QUANTITY,
        pricingMethod: UnitPricePricingMethod.QUANTITY_RANGE,
      });
      setTierData([]);
      setPricingMethod(UnitPricePricingMethod.QUANTITY_RANGE);
    }
  };

  const { run: runAutoSplitQuantity, cancel: cancelAutoSplitQuantity } =
    useDebounceFn(
      (
        { index, level, value }: AutoSplitQuantity,
        form: FormInstance,
        isSupplier: boolean,
        changeHandler: (d: UnitPriceSupplierTier[]) => void,
        tierIndex: number
      ) => {
        autoSplitQuantity(
          { index, level, value },
          supplierUnitPriceForm,
          true,
          changeHandler,
          tierIndex
        );
      },
      { wait: DEBOUNCE_TIME }
    );

  const autoSplitQuantity = (
    { index, level, value }: AutoSplitQuantity,
    form: FormInstance,
    isSupplier: boolean,
    tierHandler?: (tierData: UnitPriceSupplierTier[]) => void,
    tierIndex?: number
  ) => {
    const x = ({ index, level, value }: AutoSplitQuantity, d?: Ranges): any => {
      const formData =
        tierIndex !== undefined
          ? form.getFieldsValue().suppliers?.[tierIndex]
          : form.getFieldsValue();
      const allData = d || formData;
      const { quantityRanges: ranges } = allData;
      const maxQ = form.getFieldsValue().maximumQuantity;
      const qLen = ranges.length - 1,
        curQ = ranges[index],
        preQ = ranges[index - 1],
        nextQ = ranges[index + 1];
      const isLastLine = qLen === index;
      const insertIndex = index + 1;
      const { low, high } = rangeTypeEnum;
      const isHighLevel = level === high;
      const isLowLevel = level === low;
      if (isHighLevel) {
        curQ.highest = value;
      } else if (isLowLevel) {
        curQ.lowest = value;
      }
      if (isLowLevel) {
        if (index === 0) {
          ranges[0].lowest = 1;
        } else {
          if (curQ.lowest >= curQ.highest) {
            curQ.lowest = curQ.highest - 1;
            x(
              { index: index - 1, level: high, value: curQ.lowest - 1 },
              allData
            );
          } else {
            if (curQ.lowest <= 1) {
              ranges.splice(0, index);
            } else if (curQ.lowest > preQ.highest) {
              ranges.splice(
                index,
                0,
                initRange(curQ.lowest - 1, preQ.highest + 1, isSupplier)
              );
            } else {
              let highQIndex = 0,
                oldQIndex = index;
              while (oldQIndex > 0) {
                --oldQIndex;
                if (
                  curQ.lowest > ranges[oldQIndex].highest ||
                  curQ.highest === nextQ.highest
                ) {
                  highQIndex = oldQIndex;
                  oldQIndex = 0;
                }
              }
              ranges.splice(highQIndex + 1, index - highQIndex - 1);
              ranges[highQIndex].highest = curQ.lowest - 1;
            }
          }
        }
      } else if (isHighLevel) {
        const setNextRange = () =>
          x({ index: index + 1, level: low, value: curQ.highest + 1 }, allData);
        if (isLastLine) {
          if (curQ.highest >= maxQ) {
            curQ.highest = maxQ;
          } else {
            if (curQ.highest <= curQ.lowest) {
              curQ.highest = maxQ;
            } else {
              ranges.push(initRange(maxQ, curQ.highest + 1, isSupplier));
            }
          }
        } else {
          if (curQ.highest >= maxQ) {
            ranges.splice(insertIndex);
            ranges[ranges.length - 1].highest = maxQ;
          } else {
            if (curQ.highest <= curQ.lowest) {
              curQ.highest = curQ.lowest + 1;
              setNextRange();
            } else {
              if (curQ.highest >= nextQ.lowest) {
                let lowQIndex = index,
                  oldQIndex = index;
                while (oldQIndex < qLen) {
                  oldQIndex++;
                  if (curQ.highest > ranges[oldQIndex].lowest) {
                    lowQIndex = oldQIndex;
                  }
                }
                ranges.splice(index + 1, lowQIndex - index - 1);
                ranges[index + 1].lowest = curQ.highest + 1;
              } else {
                ranges.splice(
                  insertIndex,
                  0,
                  initRange(nextQ.lowest - 1, curQ.highest + 1, isSupplier)
                );
              }
            }
          }
        }
      }
      return allData;
    };

    const newAllData = x({ index, level, value });
    if (tierIndex !== undefined) {
      const data = form.getFieldsValue().suppliers;
      data.splice(tierIndex, 1, newAllData);
      /* TODO: async ? modify the array by index to take effect ？ */
      // tierHandler?.(clone(data));
      /* there is a delay，change event */
      tierData[tierIndex] = newAllData;
      form.setFieldsValue({ suppliers: data });
      return;
    }
  };

  const addDefaultTier = (id: number, tierKey: number) => {
    if (!!tierData.length) {
      if (pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY) {
        tierData[tierKey] = initSupplierTier(
          id,
          [],
          [initSupplierTierFixedQuantity(Math.random())]
        );
      } else {
        tierData[tierKey] = initSupplierTier(id, [
          initRange(
            supplierUnitPriceForm.getFieldsValue().maximumQuantity ||
              MAX_QUANTITY,
            1,
            true
          ) as UnitPriceSupplierTierRange,
        ]);
      }
      setTierData(tierData);
      supplierUnitPriceForm.setFieldsValue({
        suppliers: tierData,
      });

      setCollapseKeys(collapseKeys.concat(tierKey.toString()));
    }
  };

  const renderCollapseHeader = (key: number, tier?: UnitPriceSupplierTier) => {
    if (!tier) {
      return <React.Fragment key={key} />;
    }
    const quantityLen = tier?.quantityRanges?.length || 0;
    const fixedQuantityLen = tier?.fixedQuantity?.length || 0;
    const count =
      pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY
        ? fixedQuantityLen
        : quantityLen;
    const { STANDALONE, CONTINUOUS } = UnitPriceSupplierTiersOption;
    const open = collapseKeys.includes(key.toString());
    return (
      <RevxFlexLayout
        className="panel-header"
        justify="space-between"
        key={key}
      >
        <RevxFlexLayout className="panel-header__main">
          <div className="mr-100 bold">Supplier{key + 1}</div>
          <Form.Item
            name={[key, 'orgId']}
            label="Select supplier name"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Choose supplier name"
              options={supplierOptions}
              onChange={async (e) => {
                const supplierOrgId = e as number;
                if (tier?.option === STANDALONE) {
                  addDefaultTier(supplierOrgId, key);
                }
              }}
            />
          </Form.Item>
          {pricingMethod !== UnitPricePricingMethod.FIXED_QUANTITY && (
            <div className="quantity-model mr-50 ml-50">
              <Form.Item name={[key, 'option']}>
                <Radio.Group
                  onChange={(v) => {
                    let mergeData = {};
                    if (v.target.value === STANDALONE) {
                      mergeData = { quantityRanges: [], option: STANDALONE };
                    } else {
                      mergeData = {
                        quantityRanges: [
                          initRange(
                            supplierUnitPriceForm.getFieldsValue()
                              .maximumQuantity,
                            1,
                            true
                          ),
                        ],
                        option: CONTINUOUS,
                      };
                    }
                    tierData[key] = { ...tierData[key], ...mergeData };
                    setTierData(clone(tierData));
                    supplierUnitPriceForm.setFieldsValue({
                      suppliers: tierData,
                    });
                  }}
                >
                  <Radio value={UnitPriceSupplierTiersOption.STANDALONE}>
                    {t('platform.sku.unitPrice.quantityModelRadio.alone')}
                  </Radio>
                  <Radio value={UnitPriceSupplierTiersOption.CONTINUOUS}>
                    {t('platform.sku.unitPrice.quantityModelRadio.continue')}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          )}
          <div
            className={
              pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY
                ? 'ml-30'
                : ''
            }
          >
            <UnderlineText
              color="green"
              onClick={() => {
                toggleTiersHandler(key);
              }}
            >
              {/*{!quantityLen
                ? 'Add pricing tiers'
                : `Update ${quantityLen} pricing tiers`}*/}
              {open ? 'Collapse pricing tiers' : `Edit ${count} pricing tiers`}
            </UnderlineText>
          </div>
        </RevxFlexLayout>
        <div
          className="panel-header__close click"
          onClick={() => delSupplierTier(key)}
        >
          X
        </div>
      </RevxFlexLayout>
    );
  };

  const renderCollapseContent = (
    tierKey: number,
    tier?: UnitPriceSupplierTier
  ) => {
    if (!tier) {
      return <React.Fragment key={tierKey} />;
    }
    const showActionBtn =
      tier.option === UnitPriceSupplierTiersOption.STANDALONE;
    if (pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY) {
      return (
        <RevxFlexLayout className="panel-content" key={tierKey}>
          {tier?.fixedQuantity?.map((i, index) => {
            const genNameField = (name: string) => [
              tierKey,
              'fixedQuantity',
              index,
              name,
            ];
            return (
              <RevxFlexLayout
                className="panel-content__main mb-10"
                key={index}
                justify="justify-start"
                align="center"
              >
                <RevxFlexLayout>
                  <RevxFlexLayout className="quantity-block mr-100">
                    <Form.Item
                      name={genNameField('quantity')}
                      label="Quantity"
                      className="mr-24"
                      dependencies={['maximumQuantity']}
                      rules={[
                        {
                          validator: (_, value) => {
                            const maxQuantity =
                              supplierUnitPriceForm.getFieldValue(
                                'maximumQuantity'
                              );
                            if (value !== undefined && value !== null) {
                              if (value <= 0) {
                                return Promise.reject(
                                  new Error('Must be greater than 0')
                                );
                              }
                              if (
                                maxQuantity !== undefined &&
                                maxQuantity !== null &&
                                value > maxQuantity
                              ) {
                                return Promise.reject(
                                  new Error(
                                    `Must be less than or equal to ${maxQuantity}`
                                  )
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <RevxInputNumber precision={0} min={1} />
                    </Form.Item>
                  </RevxFlexLayout>
                  <RevxFlexLayout className="pricing-block mr-100">
                    <div className="label">Pricing engine price</div>
                    {i.pricingEnginePrice !== null ? (
                      <RevxMoney
                        value={i.pricingEnginePrice}
                        decimalDigits={6}
                      />
                    ) : (
                      NOT_APPLICABLE
                    )}
                  </RevxFlexLayout>
                  <RevxFlexLayout className="pricing-block mr-100">
                    <Form.Item
                      name={genNameField('priceToPaySupplier')}
                      label="Price to pay supplier"
                    >
                      <RevxInputNumber prefix="$" precision={6} min={0} />
                    </Form.Item>
                  </RevxFlexLayout>
                </RevxFlexLayout>
                {showActionBtn && (
                  <RevxFlexLayout
                    className="quantity-close click"
                    onClick={() => delSupplierTierQuantityRange(tierKey, index)}
                  >
                    <FileDeleteIcon />
                  </RevxFlexLayout>
                )}
              </RevxFlexLayout>
            );
          })}
          {showActionBtn && (
            <RevxFlexLayout className="panel-content__footer" key={tierKey}>
              <UnderlineText
                color="green"
                onClick={() => addSupplierTierQuantityRange(tierKey)}
              >
                +Add quantity
              </UnderlineText>
            </RevxFlexLayout>
          )}
        </RevxFlexLayout>
      );
    }
    return (
      <RevxFlexLayout className="panel-content" key={tierKey}>
        {tier?.quantityRanges?.map((i, index) => {
          const genNameField = (name: string) => [
            tierKey,
            'quantityRanges',
            index,
            name,
          ];
          return (
            <RevxFlexLayout
              className="panel-content__main mb-10"
              key={index}
              justify="justify-start"
              align="bottom"
            >
              <RevxFlexLayout>
                <RevxFlexLayout className="quantity-block mr-100">
                  <Form.Item
                    name={genNameField('lowest')}
                    label="Lowest Quantity"
                    className="mr-24"
                  >
                    <RevxInputNumber
                      precision={0}
                      min={0}
                      onChange={(value) => {
                        if (showActionBtn) return;
                        cancelAutoSplitQuantity();
                        runAutoSplitQuantity(
                          { index, level: rangeTypeEnum.low, value },
                          supplierUnitPriceForm,
                          true,
                          setTierData,
                          tierKey
                        );
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={genNameField('highest')}
                    label="Highest Quantity"
                  >
                    <RevxInputNumber
                      min={0}
                      precision={0}
                      onChange={(value) => {
                        if (showActionBtn) return;
                        cancelAutoSplitQuantity();
                        runAutoSplitQuantity(
                          { index, level: rangeTypeEnum.high, value },
                          supplierUnitPriceForm,
                          true,
                          setTierData,
                          tierKey
                        );
                      }}
                    />
                  </Form.Item>
                </RevxFlexLayout>
                <RevxFlexLayout className="pricing-block mr-100">
                  <div className="label">Pricing engine price</div>
                  {i.pricingEnginePrice !== null ? (
                    <RevxMoney value={i.pricingEnginePrice} decimalDigits={6} />
                  ) : (
                    NOT_APPLICABLE
                  )}
                </RevxFlexLayout>
                <RevxFlexLayout className="pricing-block mr-100">
                  <Form.Item
                    name={genNameField('priceToPaySupplier')}
                    label="Price to pay supplier"
                  >
                    <RevxInputNumber prefix="$" precision={6} min={0} />
                  </Form.Item>
                </RevxFlexLayout>
              </RevxFlexLayout>
              {showActionBtn && (
                <RevxFlexLayout
                  className="quantity-close click"
                  onClick={() => delSupplierTierQuantityRange(tierKey, index)}
                >
                  <FileDeleteIcon />
                </RevxFlexLayout>
              )}
            </RevxFlexLayout>
          );
        })}
        {showActionBtn && (
          <RevxFlexLayout className="panel-content__footer" key={tierKey}>
            <UnderlineText
              color="green"
              onClick={() => addSupplierTierQuantityRange(tierKey)}
            >
              +Add quantity range
            </UnderlineText>
          </RevxFlexLayout>
        )}
      </RevxFlexLayout>
    );
  };

  const renderFixedQuantityGrid = () => {
    const preferredSuppliers = getPreferredSuppliers();
    const rowCount = tierData?.[0]?.fixedQuantity?.length || 0;
    const supplierName = (orgId?: number) =>
      supplierOptions.find((option) => option.value === orgId)?.label;
    /* only the suppliers on screen can be preferred, and the column select already keeps them unique,
       so this list must not carry the `disabled` the column select puts on the ones already taken */
    const preferredOptions = tierData
      .filter((tier) => !!tier.orgId)
      .map((tier) => ({
        value: tier.orgId as number,
        label: supplierName(tier.orgId) || tier.orgId,
      }));
    const enginePriceOf = (rowIndex: number) => {
      const preferredOrgId = preferredSuppliers[rowIndex]?.orgId;
      const columns = preferredOrgId
        ? tierData.filter((tier) => tier.orgId === preferredOrgId)
        : tierData;
      return columns
        .map((tier) => tier.fixedQuantity?.[rowIndex]?.pricingEnginePrice)
        .find((price) => price !== null && price !== undefined);
    };
    return (
      <div className="fixed-quantity-grid mb-24">
        <RevxFlexLayout
          className="fixed-quantity-grid__row mb-24"
          align="bottom"
        >
          <div className="fixed-quantity-grid__quantity bold">
            Select Supplier(s)
          </div>
          <div className="fixed-quantity-grid__engine" />
          {tierData.map((tier, columnIndex) => (
            <div className="fixed-quantity-grid__cell" key={columnIndex}>
              <RevxFlexLayout align="center">
                <Form.Item
                  name={['suppliers', columnIndex, 'orgId']}
                  rules={[{ required: true }]}
                  noStyle
                >
                  <Select
                    placeholder="Choose supplier name"
                    options={supplierOptions}
                  />
                </Form.Item>
                <span
                  className="fixed-quantity-grid__remove click"
                  onClick={() => delGridSupplier(columnIndex)}
                >
                  x
                </span>
              </RevxFlexLayout>
            </div>
          ))}
          <div className="fixed-quantity-grid__preferred">
            <UnderlineText color="green" onClick={addGridSupplier}>
              +<span>Add supplier</span>
            </UnderlineText>
          </div>
        </RevxFlexLayout>

        <RevxFlexLayout
          className="fixed-quantity-grid__row mb-10"
          align="bottom"
        >
          <div className="fixed-quantity-grid__quantity bold">Quantity:</div>
          <div className="fixed-quantity-grid__engine bold">
            Pricing engine price
          </div>
          {tierData.map((tier, columnIndex) => (
            <div className="fixed-quantity-grid__cell bold" key={columnIndex}>
              Price to pay supplier:
            </div>
          ))}
          <div className="fixed-quantity-grid__preferred bold">
            Preferred Supplier:
          </div>
          <div className="fixed-quantity-grid__close" />
        </RevxFlexLayout>

        {Array.from({ length: rowCount }).map((r, rowIndex) => (
          <RevxFlexLayout
            className="fixed-quantity-grid__row mb-10"
            align="center"
            key={rowIndex}
          >
            <div className="fixed-quantity-grid__quantity">
              <Form.Item
                name={['suppliers', 0, 'fixedQuantity', rowIndex, 'quantity']}
                dependencies={['maximumQuantity']}
                noStyle
                rules={[
                  { required: true },
                  {
                    validator: (_, value) => {
                      const maxQuantity =
                        supplierUnitPriceForm.getFieldValue('maximumQuantity');
                      if (value !== undefined && value !== null) {
                        if (value <= 0) {
                          return Promise.reject(
                            new Error('Must be greater than 0')
                          );
                        }
                        if (
                          maxQuantity !== undefined &&
                          maxQuantity !== null &&
                          value > maxQuantity
                        ) {
                          return Promise.reject(
                            new Error(
                              `Must be less than or equal to ${maxQuantity}`
                            )
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <RevxInputNumber
                  precision={0}
                  min={1}
                  onChange={(value) => syncGridQuantity(rowIndex, value)}
                />
              </Form.Item>
            </div>
            <div className="fixed-quantity-grid__engine">
              {enginePriceOf(rowIndex) !== null &&
              enginePriceOf(rowIndex) !== undefined ? (
                <RevxMoney
                  value={enginePriceOf(rowIndex) as number}
                  decimalDigits={6}
                />
              ) : (
                NOT_APPLICABLE
              )}
            </div>
            {tierData.map((tier, columnIndex) => (
              <div className="fixed-quantity-grid__cell" key={columnIndex}>
                <Form.Item
                  name={[
                    'suppliers',
                    columnIndex,
                    'fixedQuantity',
                    rowIndex,
                    'priceToPaySupplier',
                  ]}
                  noStyle
                >
                  <RevxInputNumber
                    prefix="$"
                    precision={6}
                    min={0}
                    allowEmpty
                    keepEmptyOnBlur
                  />
                </Form.Item>
              </div>
            ))}
            <div className="fixed-quantity-grid__preferred">
              <Form.Item
                name={['preferredSuppliers', rowIndex, 'orgId']}
                noStyle
              >
                <Select
                  placeholder="Cheapest supplier"
                  options={preferredOptions}
                  allowClear
                />
              </Form.Item>
            </div>
            <div
              className="fixed-quantity-grid__close click"
              onClick={() => delGridQuantity(rowIndex)}
            >
              <FileDeleteIcon />
            </div>
          </RevxFlexLayout>
        ))}
        {!!tierData.length && (
          <UnderlineText color="green" onClick={addGridQuantity}>
            +<span>Add Quantity</span>
          </UnderlineText>
        )}
      </div>
    );
  };

  const collapseChange = (key: string | string[]) => {
    setCollapseKeys(isArray(key) ? key : [key]);
  };

  const toggleTiersHandler = (key: number) => {
    const k = key.toString();
    if (collapseKeys.includes(k)) {
      pull(collapseKeys, k);
    } else {
      collapseKeys.push(k);
    }
    setCollapseKeys(clone(collapseKeys));
  };

  const addSupplierTier = () => {
    tierData?.push(initSupplierTier());
    setTierDataHandler(tierData);
  };

  const delSupplierTier = (tierKey: number) => {
    tierData.splice(tierKey, 1);
    setTierDataHandler(tierData);
  };

  const addSupplierTierQuantityRange = (tierKey: number) => {
    const tier = tierData[tierKey];
    if (tier) {
      if (pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY) {
        if (!tier.fixedQuantity) {
          tier.fixedQuantity = [];
        }
        tier.fixedQuantity.push(initSupplierTierFixedQuantity(Math.random()));
      } else {
        if (!tier.quantityRanges) {
          tier.quantityRanges = [];
        }
        tier.quantityRanges.push(initSupplierTierQuantityRange(Math.random()));
      }
      setTierDataHandler(tierData);
    }
  };

  const delSupplierTierQuantityRange = (
    tierKey: number,
    rangeIndex: number
  ) => {
    const tier = tierData[tierKey];
    if (tier) {
      if (pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY) {
        if (tier.fixedQuantity) {
          tier.fixedQuantity.splice(rangeIndex, 1);
        }
      } else {
        if (tier.quantityRanges) {
          tier.quantityRanges.splice(rangeIndex, 1);
        }
      }
      setTierDataHandler(tierData);
    }
  };

  /* ------------------------------- fixed quantity grid -------------------------------
     one line per quantity, one column per supplier, and the supplier to buy each quantity from. Every
     column carries a cell for every line so the lines stay side by side, blank where there is no price. */

  const isFixedQuantity =
    pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY;

  const getPreferredSuppliers = (): UnitPricePreferredSupplier[] =>
    supplierUnitPriceForm.getFieldValue('preferredSuppliers') || [];

  const setGridDataHandler = (
    suppliers: UnitPriceSupplierTier[],
    preferredSuppliers: UnitPricePreferredSupplier[]
  ) => {
    setTierData(clone(suppliers));
    supplierUnitPriceForm.setFieldsValue({ suppliers, preferredSuppliers });
    formValuesChangeHandler('null', supplierUnitPriceForm.getFieldsValue());
  };

  const gridQuantities = (): (number | null)[] =>
    (tierData?.[0]?.fixedQuantity || []).map((i) => i.quantity);

  const addGridQuantity = () => {
    if (!tierData?.length) return;
    const suppliers = tierData.map((tier) => ({
      ...tier,
      fixedQuantity: (tier.fixedQuantity || []).concat({
        quantity: null as any,
        priceToPaySupplier: null,
        pricingEnginePrice: null,
        id: Math.random(),
      }),
    }));
    setGridDataHandler(
      suppliers,
      getPreferredSuppliers().concat({ quantity: null, orgId: undefined })
    );
  };

  const delGridQuantity = (rowIndex: number) => {
    const suppliers = tierData.map((tier) => ({
      ...tier,
      fixedQuantity: (tier.fixedQuantity || []).filter(
        (i, index) => index !== rowIndex
      ),
    }));
    setGridDataHandler(
      suppliers,
      getPreferredSuppliers().filter((i, index) => index !== rowIndex)
    );
  };

  /* the quantity belongs to the line, not to one supplier: every column prices the same quantity */
  const syncGridQuantity = (rowIndex: number, quantity: number) => {
    const suppliers = (
      supplierUnitPriceForm.getFieldValue('suppliers') || []
    ).map((tier: UnitPriceSupplierTier) => ({
      ...tier,
      fixedQuantity: (tier.fixedQuantity || []).map((i, index) =>
        index === rowIndex ? { ...i, quantity } : i
      ),
    }));
    const preferredSuppliers = getPreferredSuppliers().map((i, index) =>
      index === rowIndex ? { ...i, quantity } : i
    );
    setTierData(clone(suppliers));
    supplierUnitPriceForm.setFieldsValue({ suppliers, preferredSuppliers });
  };

  const addGridSupplier = () => {
    const quantities = gridQuantities();
    setGridDataHandler(
      tierData.concat(
        initSupplierTier(
          undefined,
          [],
          quantities.map((quantity) => ({
            quantity: quantity as number,
            priceToPaySupplier: null,
            pricingEnginePrice: null,
            id: Math.random(),
          }))
        )
      ),
      getPreferredSuppliers()
    );
  };

  const delGridSupplier = (columnIndex: number) => {
    const removed = tierData[columnIndex]?.orgId;
    setGridDataHandler(
      tierData.filter((tier, index) => index !== columnIndex),
      /* a line preferring the supplier that just went away falls back to the cheapest one again */
      getPreferredSuppliers().map((i) =>
        i.orgId === removed ? { ...i, orgId: undefined } : i
      )
    );
  };

  const setTierDataHandler = (tierData: UnitPriceSupplierTier[]) => {
    setTierData(clone(tierData));
    supplierUnitPriceForm.setFieldsValue({ suppliers: tierData });
    formValuesChangeHandler('null', supplierUnitPriceForm.getFieldsValue());
  };

  const {
    run: runUpdateSupplierPriceHandler,
    cancel: cancelUpdateSupplierPriceHandler,
  } = useDebounceFn(
    async () => {
      await updateSupplierPrice(false);
    },
    { wait: DEBOUNCE_TIME + 200 }
  );

  /* the fields the pricing model hides are unmounted, and getFieldsValue() only returns mounted ones -
     leaving them out of the payload lets the API defaults overwrite what is stored */
  const getSubmitValues = () => {
    const values = {
      ...supplierUnitPriceForm.getFieldsValue(),
      pricingMethod: supplierUnitPriceForm.getFieldValue('pricingMethod'),
      maximumQuantity: supplierUnitPriceForm.getFieldValue('maximumQuantity'),
      suppliers: supplierUnitPriceForm.getFieldValue('suppliers'),
      preferredSuppliers:
        supplierUnitPriceForm.getFieldValue('preferredSuppliers') || [],
    };
    values.preferredSuppliers = (values.preferredSuppliers || []).filter(
      (i: UnitPricePreferredSupplier) =>
        !!i?.orgId && i?.quantity !== null && i?.quantity !== undefined
    );
    return values;
  };

  const updateSupplierPrice = async (showError: boolean = true) => {
    try {
      await supplierUnitPriceForm.validateFields();
      setLoading(true);
      const d = getSubmitValues();
      let validateFlag = true;
      if (d.pricingModel === UnitPricePricingModel.UNIT_PRICE) {
        d.suppliers?.forEach((i: UnitPriceSupplierTier) => {
          if (!i.orgId) {
            validateFlag = false;
          }
        });
      }
      if (!validateFlag) return;
      const updateData = await skuService.updateSupplierUnitPrice(
        skuId,
        d,
        showError
      );
      /* a response for a model the user already switched away from would
         wipe what is on screen */
      if (
        updateData.pricingModel !==
        supplierUnitPriceForm.getFieldValue('pricingModel')
      ) {
        return;
      }
      const aligned = alignFixedQuantityGrid(updateData);
      supplierUnitPriceForm.setFieldsValue(aligned);
      setTierData(aligned.suppliers);
    } catch (e) {
      if (showError) {
        message.error('Please check your input.');
      }
    } finally {
      setLoading(false);
    }
  };

  const savePrice = async () => {
    supplierUnitPriceForm
      .validateFields()
      .then(async (res) => {
        await updateSupplierPrice();
      })
      .catch((e) => {
        message.error('Please check your input.');
      });
  };

  const formValuesChangeHandler = async (
    e: any,
    allValues: UnitPriceSupplier
  ) => {
    /* allValues only carries the mounted fields, so read the ones the pricing model hides from the store */
    const storedSuppliers: UnitPriceSupplierTier[] =
      supplierUnitPriceForm.getFieldValue('suppliers');
    if (Object.keys(e).includes('pricingModel')) {
      if (e.pricingModel === UnitPricePricingModel.PRICING_ENGINE) {
        if (storedSuppliers?.length) {
          unitPriceCacheRef.current = {
            suppliers: cloneDeep(storedSuppliers),
            maximumQuantity:
              supplierUnitPriceForm.getFieldValue('maximumQuantity'),
            pricingMethod: supplierUnitPriceForm.getFieldValue('pricingMethod'),
          };
        }
      } else if (!storedSuppliers?.length && unitPriceCacheRef.current) {
        const cache = unitPriceCacheRef.current;
        supplierUnitPriceForm.setFieldsValue({
          suppliers: cloneDeep(cache.suppliers),
          maximumQuantity: cache.maximumQuantity ?? MAX_QUANTITY,
          pricingMethod:
            cache.pricingMethod || UnitPricePricingMethod.QUANTITY_RANGE,
        });
        setTierData(supplierUnitPriceForm.getFieldValue('suppliers'));
      }
    }
    if (Object.keys(e).includes('suppliers')) {
      /* read from the store: allValues drops what is not on screen, `option` and the quantity
         ranges of the other pricing method among it */
      setTierData(supplierUnitPriceForm.getFieldValue('suppliers'));
    }
    setShowRanges(allValues.pricingModel === UnitPricePricingModel.UNIT_PRICE);
    setPricingMethod(
      supplierUnitPriceForm.getFieldValue('pricingMethod') ||
        UnitPricePricingMethod.QUANTITY_RANGE
    );
    cancelUpdateSupplierPriceHandler();
    await runUpdateSupplierPriceHandler();
    const k = supplierUnitPriceForm
      .getFieldValue('suppliers')
      ?.map((i: UnitPriceSupplierTier) => {
        return i.orgId || 0;
      });
    setSelectSupplierOptions(k);
  };

  return (
    <RevxSpin spinning={loading}>
      <div className="unit-price">
        {header()}
        <div className="unit-price__title">
          <h2>{t('platform.sku.unitPrice.supplierPrice')}</h2>
          <div className="bold">{t('platform.sku.unitPrice.pricingTips')}</div>
        </div>
        <Form
          form={supplierUnitPriceForm}
          requiredMark={false}
          onValuesChange={formValuesChangeHandler}
        >
          <Form.Item
            name="pricingModel"
            label={t('platform.sku.unitPrice.priceModelLabel')}
          >
            <Radio.Group>
              <Radio value={UnitPricePricingModel.PRICING_ENGINE}>
                {t('platform.sku.unitPrice.priceModelRadio.pricing')}
              </Radio>
              <Radio value={UnitPricePricingModel.UNIT_PRICE}>
                {t('platform.sku.unitPrice.priceModelRadio.unit')}
              </Radio>
            </Radio.Group>
          </Form.Item>
          {showRanges && (
            <>
              {isPart && (
                <div className="unit-price__download">
                  <a href={skuService.getSupplierUnitPriceExcel(skuId)}>
                    <UnderlineText color="green">
                      Download spreadsheet
                    </UnderlineText>
                  </a>
                  <span> with supplier pricing tiers for each SKU</span>
                </div>
              )}
              <RevxFlexLayout className="unit-price__quantity mb-24">
                <Form.Item
                  name="pricingMethod"
                  label="Pricing method"
                  className="mr-50"
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Radio value={UnitPricePricingMethod.FIXED_QUANTITY}>
                      Fixed quantity increments
                    </Radio>
                    <Radio value={UnitPricePricingMethod.QUANTITY_RANGE}>
                      Quantity range
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="maximumQuantity"
                  label={t('platform.sku.unitPrice.maxQuantityLabel')}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (
                          value !== undefined &&
                          value !== null &&
                          value <= 0
                        ) {
                          return Promise.reject(
                            new Error('Must be greater than 0')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <RevxInputNumber precision={0} min={1} />
                </Form.Item>
              </RevxFlexLayout>
              {isFixedQuantity && renderFixedQuantityGrid()}
              {!isFixedQuantity && (
                <Form.List name="suppliers">
                  {(fields, operation, meta) => {
                    return (
                      <Collapse
                        onChange={collapseChange}
                        activeKey={collapseKeys}
                        collapsible={'disabled'}
                        className="mb-24"
                      >
                        {fields.map((f) => {
                          return (
                            <React.Fragment key={f.key}>
                              <Collapse.Panel
                                header={renderCollapseHeader(
                                  f.key,
                                  tierData?.[f.key]
                                )}
                                key={f.key}
                                showArrow={false}
                              >
                                {renderCollapseContent(
                                  f.key,
                                  tierData?.[f.key]
                                )}
                              </Collapse.Panel>
                            </React.Fragment>
                          );
                        })}
                      </Collapse>
                    );
                  }}
                </Form.List>
              )}
            </>
          )}
        </Form>
        {showRanges && !isFixedQuantity && (
          <div className="add-btn mb-24">
            <UnderlineText color="green" onClick={addSupplierTier}>
              +<span>Add supplier</span>
            </UnderlineText>
          </div>
        )}
        {footer({ onSave: savePrice })}
      </div>
    </RevxSpin>
  );
};
